import type { InvitationPreview, MeResponse } from '@agent-platform/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';
import { ErrorAlert } from '@/components/error-alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { api, ApiError } from '@/lib/api';
import { ROLE_LABELS } from '@/lib/format';
import { useApplyMe } from '@/lib/queries';

const route = getRouteApi('/invite/$token');
const MIN_PASSWORD = 12;

function FieldMessage({ id, message }: { id: string; message?: string }) {
  return message ? (
    <p id={id} className="text-destructive text-sm">
      {message}
    </p>
  ) : null;
}

export function InvitePage() {
  const { token } = route.useParams();
  const navigate = useNavigate();
  const applyMe = useApplyMe();
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const preview = useQuery({
    queryKey: ['invitation', token],
    queryFn: () =>
      api<InvitationPreview>('/public/invitations/preview', { method: 'POST', body: { token } }),
    retry: false,
  });

  const accept = useMutation({
    mutationFn: () =>
      api<MeResponse>('/public/invitations/accept', {
        method: 'POST',
        body: preview.data?.userExists ? { token, password } : { token, name, password },
      }),
    onSuccess: (me) => {
      applyMe(me);
      void navigate({ to: '/' });
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setLocalError(null);
    if (!preview.data?.userExists) {
      if (password.length < MIN_PASSWORD)
        return setLocalError(`Password must be at least ${MIN_PASSWORD} characters`);
      if (password !== confirm) return setLocalError('Passwords do not match');
    }
    accept.mutate();
  };

  const fieldErr = (path: string) =>
    accept.error instanceof ApiError ? accept.error.fieldError(path) : undefined;

  return (
    <div className="bg-muted/40 flex min-h-svh items-center justify-center p-4">
      <Card className="w-full max-w-md">
        {preview.isPending && (
          <CardContent className="grid gap-3 py-8">
            <Skeleton className="h-6 w-2/3" />
            <Skeleton className="h-4 w-full" />
          </CardContent>
        )}
        {preview.isError && (
          <>
            <CardHeader>
              <CardTitle>Invitation unavailable</CardTitle>
              <CardDescription>
                {preview.error instanceof ApiError && preview.error.status === 404
                  ? 'This invitation link is invalid, has expired, or was already used. Ask the person who invited you for a new link.'
                  : 'We could not load this invitation. Please try again.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild variant="outline">
                <Link to="/login">Go to sign in</Link>
              </Button>
            </CardContent>
          </>
        )}
        {preview.data && (
          <>
            <CardHeader>
              <CardTitle className="text-xl">Join {preview.data.tenantName}</CardTitle>
              <CardDescription>
                You were invited as <strong>{ROLE_LABELS[preview.data.role]}</strong> using{' '}
                {preview.data.email}.
                {preview.data.userExists
                  ? ' Enter the password for your existing account to accept.'
                  : ' Create your account to accept.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={submit} className="grid gap-4" noValidate>
                <ErrorAlert
                  error={localError ? new ApiError(400, { detail: localError }) : accept.error}
                />
                {!preview.data.userExists && (
                  <div className="grid gap-2">
                    <Label htmlFor="name">Your name</Label>
                    <Input
                      id="name"
                      autoComplete="name"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      aria-describedby="name-error"
                    />
                    <FieldMessage id="name-error" message={fieldErr('name')} />
                  </div>
                )}
                <div className="grid gap-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete={preview.data.userExists ? 'current-password' : 'new-password'}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    aria-describedby="password-error"
                  />
                  <FieldMessage id="password-error" message={fieldErr('password')} />
                </div>
                {!preview.data.userExists && (
                  <div className="grid gap-2">
                    <Label htmlFor="confirm">Confirm password</Label>
                    <Input
                      id="confirm"
                      type="password"
                      autoComplete="new-password"
                      required
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                    />
                  </div>
                )}
                <Button type="submit" disabled={accept.isPending}>
                  {accept.isPending ? 'Accepting…' : 'Accept invitation'}
                </Button>
              </form>
            </CardContent>
          </>
        )}
      </Card>
    </div>
  );
}
